import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Inject,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { TableColumn } from 'src/app/shared/models';
import {
  loadContacts,
  resetContactsListState,
  updateContact,
} from './store/actions/contact-duplicate-cell-phone.actions';
import {
  selectContacts,
  selectContactsFilter,
  selectContactsListPageLoading,
} from './store/selectors/contact-duplicate-cell-phone.selectors';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { LayoutUtility } from 'src/app/shared/utils';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'kt-contact-duplicate-cell-number',
  templateUrl: './contact-duplicate-cell-number.component.html',
  styleUrls: ['./contact-duplicate-cell-number.component.scss'],
})
export class ContactDuplicateCellNumberComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private originalContacts: any[] = [];
  private formGroup: FormGroup;
  loadContactsList = loadContacts;
  resetContactsListState = resetContactsListState;
  selectContactsFilter = selectContactsFilter;
  selectContacts = selectContacts;
  selectContactsListPageLoading = selectContactsListPageLoading;
  resetNotificationListStateAction = resetContactsListState;

  statusFilterOptions = [
    { id: 1, label: 'All', value: '' },
    { id: 2, label: 'Active', value: 'true' },
    { id: 3, label: 'Inactive', value: 'false' },
  ];
  contacts: any[] = [];
  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<ContactDuplicateCellNumberComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<any>,
    private layoutUtilsService: LayoutUtility,
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
  ) {}

  columns: TableColumn[] = [];

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('search', { static: true }) searchInput: ElementRef;
  @ViewChild('idFilterRef', { static: true }) idFilterRef: TemplateRef<any>;
  @ViewChild('descriptionFilterRef', { static: true }) descriptionFilterRef: TemplateRef<any>;
  @ViewChild('sourceFilterRef', { static: true }) sourceFilterRef: TemplateRef<any>;
  @ViewChild('cellPhoneRef', { static: true }) cellPhoneRef: TemplateRef<any>;
  @ViewChild('contactRef', { static: true }) contactRef: TemplateRef<any>;
  @ViewChild('categoryRef', { static: true }) categoryRef: TemplateRef<any>;
  @ViewChild('typeRef', { static: true }) typeRef: TemplateRef<any>;
  @ViewChild('typeFilterRef', { static: true }) typeFilterRef: TemplateRef<any>;
  @ViewChild('usernameRef', { static: true }) usernameRef: TemplateRef<any>;
  @ViewChild('usernameFilterRef', { static: true }) usernameFilterRef: TemplateRef<any>;
  @ViewChild('statusFilterRef', { static: true }) statusFilterRef: TemplateRef<any>;
  @ViewChild('statusToggleRef', { static: true }) statusToggleRef: TemplateRef<any>;
  @ViewChild('statusRef', { static: true }) statusRef: TemplateRef<any>;
  @ViewChild('cellPhoneFilterRef', { static: true }) cellPhoneFilterRef: TemplateRef<any>;
  @ViewChild('actionsRef', { static: true }) actionsRef: TemplateRef<any>;

  ngOnInit() {
    this.formGroup = this.formBuilder.group({
      cellPhones: this.formBuilder.array([]),
    });
    const contactSubscription = this.store
      .pipe(
        select(selectContacts),
        filter((data) => data && data.items.length > 0),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev.items) === JSON.stringify(curr.items),
        ),
      )
      .subscribe(({ items }) => {
        this.contacts = [...items];
        this.originalContacts = JSON.parse(JSON.stringify(items));
        this.initializeCellPhones();
      });

    this.subscriptions.add(contactSubscription);

    this.columns = [
      {
        columnDef: 'id',
        header: 'ID',
        cell: (data: any) => data.id,
        filterTemplate: this.idFilterRef,
        filterDefaultValue: '',
        filterQueryParam: 'description',
      },
      {
        columnDef: 'contact',
        header: 'Contact',
        cell: (data: any) => data.first_name,
        filterTemplate: this.contactRef,
        filterQueryParam: 'contact',
        filterDefaultValue: '',
      },
      {
        columnDef: 'username',
        header: 'Username',
        cell: (data: any) => data.username,
        filterTemplate: this.usernameFilterRef,
        filterQueryParam: 'username',
        filterDefaultValue: '',
      },
      {
        columnDef: 'isActive',
        header: 'Status',
        cell: () => '',
        template: this.statusRef,
        toggleable: true,
        toggleTemplate: this.statusToggleRef,
        toggleOptions: this.statusFilterOptions,
        filterTemplate: this.statusFilterRef,
        filterDefaultValue: this.statusFilterOptions.find((o) => o.value === 'true'),
        filterQueryParam: 'isActive',
        filterObjectKey: 'value',
      },
      {
        columnDef: 'type/s',
        header: 'Type/s',
        cell: (data: any) => data.entity_type,
        filterTemplate: this.typeFilterRef,
        filterQueryParam: 'Type/s',
        filterDefaultValue: '',
      },
      {
        columnDef: 'cellPhone',
        header: 'Cell Phone',
        cell: () => '',
        template: this.cellPhoneFilterRef,
        filterQueryParam: 'cellPhone',
        filterDefaultValue: '',
      },
      {
        columnDef: 'actions',
        header: '',
        cell: () => '',
        template: this.actionsRef,
      },
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.store.dispatch(resetContactsListState({ saveFilter: false, filter: {} }));
  }
  initializeCellPhones() {
    const cellPhonesArray = this.formGroup.get('cellPhones') as FormArray;
    cellPhonesArray.clear();

    this.contacts.forEach((contact) => {
      const cellPhoneValue = contact.cell_phone ? contact.cell_phone.trim() : '';
      cellPhonesArray.push(this.createCellPhoneControl(cellPhoneValue));
    });

    this.cdr.detectChanges();
  }

  createCellPhoneControl(phone: string): FormControl {
    const control = this.formBuilder.control(phone, [Validators.pattern(/^[0-9]{10}$/)]);

    this.subscriptions.add(
      control.valueChanges.subscribe(() => {
        this.cdr.detectChanges();
      }),
    );

    return control;
  }
  getFormControl(index: number): FormControl {
    const cellPhonesArray = this.formGroup.get('cellPhones') as FormArray;

    if (index >= cellPhonesArray.length) {
      cellPhonesArray.push(this.createCellPhoneControl(''));
    }

    return cellPhonesArray.at(index) as FormControl;
  }

  editContact(index: number) {
    const cellPhonesArray = this.formGroup.get('cellPhones') as FormArray;
    const cellPhoneValue = cellPhonesArray.at(index).value.trim() || null;
    const originalCellPhone = this.originalContacts[index]?.cell_phone || null;

    if (cellPhoneValue === originalCellPhone) {
      this.layoutUtilsService.showActionNotification('ERROR', 'No changes detected.');
      return;
    }
    const updatedContact = { ...this.contacts[index], cell_phone: cellPhoneValue };
    this.store.dispatch(
      updateContact({ id: updatedContact.id, changes: { cell_phone: cellPhoneValue } }),
    );
  }
  resetCellPhone(index: number) {
    const cellPhonesArray = this.formGroup.get('cellPhones') as FormArray;
    cellPhonesArray.at(index).setValue(this.originalContacts[index]?.cell_phone || '');
    this.cdr.detectChanges();
  }

  close(event) {
    event.stopPropagation();

    this.dialogRef.close();
  }
}
